'use client';

import { Button, ButtonVariant } from '@components/button';
import { ScrollButton } from '@components/scrollButton';
import { Typography } from '@components/typography';
import { sendAnalyticsEvent } from '@lib/analytics/analytics';
import { classNames } from '@lib/classNames/classNames';
import { memo, useCallback } from 'react';

/**
 * Свойства компонента AbTestButton
 */
interface AbTestButtonProps {
  /**
   * Вариант отображения кнопки (primary, secondary и т.д.)
   */
  buttonVariant: ButtonVariant;

  /**
   * URL с UTM-параметрами для внешней ссылки
   */
  hrefWithUtm?: string;

  /**
   * Флаг состояния загрузки. При true кнопка скрыта с анимацией fade-out
   */
  isLoading: boolean;

  /**
   * Базовый URL без UTM-параметров
   */
  href?: string;

  /**
   * Содержимое кнопки (текст или React-элемент)
   */
  content?: string | React.ReactNode;

  /**
   * Идентификатор события для отправки в аналитику при клике
   */
  analyticsEvent?: string;
}

/**
 * Компонент кнопки для A/B тестирования с поддержкой состояния загрузки
 *
 * @description
 * Универсальный компонент кнопки, который:
 * - Поддерживает состояние загрузки с плавной анимацией fade in/out
 * - Может работать как внешняя ссылка (с href) или скролл к форме (без href)
 * - Отправляет события в аналитику при клике
 * - Используется в A/B тестах для отображения различных вариантов кнопок
 *
 * @example
 * // Кнопка с внешней ссылкой
 * <AbTestButton
 *   buttonVariant="primary"
 *   content="Перейти на сайт"
 *   href="/external"
 *   hrefWithUtm="/external?utm_source=test"
 *   analyticsEvent="button_click"
 *   isLoading={false}
 * />
 *
 * @example
 * // Кнопка-скролл к форме
 * <AbTestButton
 *   buttonVariant="secondary"
 *   content="Заполнить форму"
 *   isLoading={false}
 * />
 */
export const AbTestButton = memo(
  ({
    isLoading,
    content,
    href,
    hrefWithUtm,
    buttonVariant,
    analyticsEvent,
  }: AbTestButtonProps) => {
    const handleClick = useCallback(() => {
      if (analyticsEvent) {
        sendAnalyticsEvent(analyticsEvent);
      }
    }, [analyticsEvent]);

    return (
      <div
        className={classNames({
          cls: 'opacity-0 transition duration-300 ease-in-out max-md:w-full',
          mods: { 'opacity-100': !isLoading },
        })}
      >
        {!!content && !!hrefWithUtm && (
          <Button
            className={classNames({
              cls: 'flex h-14 w-60 items-center justify-center max-sm:w-full',
            })}
            variant={buttonVariant}
            disabled={false}
            role="button"
            ariaLabel={content as string}
            href={hrefWithUtm}
            onClick={analyticsEvent ? handleClick : undefined}
            target="_blank"
          >
            <Typography variant="accent-m" className="font-medium">
              {content}
            </Typography>
          </Button>
        )}

        {!!content && !href && (
          <ScrollButton
            target="form"
            className={classNames({
              cls: 'flex h-14 w-60 items-center justify-center max-sm:w-full',
            })}
            variant={buttonVariant}
          >
            <Typography variant="accent-m" className="font-medium">
              {content}
            </Typography>
          </ScrollButton>
        )}
      </div>
    );
  },
);

AbTestButton.displayName = 'AbTestButton';
